import { useEffect, useState, useCallback, useMemo } from 'react';
import type { StoreConfig } from '../services/types';

interface HeaderState {
  bottom: number;
  height: number;
  isVisible: boolean;
  isInViewport: boolean;
  position: string;
}

type UseHeaderTrackingReturn = {
  topStyle: string;
};

export function useHeaderTracking({
  storeConfig,
}: {
  storeConfig?: StoreConfig;
}): UseHeaderTrackingReturn {
  const [headerSelector, setHeaderSelector] = useState<string>(
    storeConfig?.searchConfig?.headerSelector || 'header'
  );

  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);

  const [headerState, setHeaderState] = useState<HeaderState>({
    bottom: 0,
    height: 0,
    isVisible: false,
    isInViewport: true, // Default to true, we rely on rect measurements now
    position: 'fixed',
  });
  // Removed separate inViewport state as it's unreliable for sticky headers

  const isVisible = headerState.isVisible;

  const galleryTopMargin =
    storeConfig?.searchConfig?.galleryTopMargin !== undefined
      ? storeConfig.searchConfig.galleryTopMargin
      : 20;

  const isHeaderSticky = useMemo(
    () =>
      headerState.position === 'sticky' ||
      headerState.position === '-webkit-sticky',
    [headerState.position]
  );

  const getTopStyle = () => {
    // If we have a measurement, position relative to the header's bottom
    if (isVisible && headerState.bottom > 0) {
      return `${headerState.bottom - galleryTopMargin}px`;
    }
    return '';
  };

  useEffect(() => {
    if (storeConfig?.searchConfig?.headerSelector) {
      setHeaderSelector(storeConfig.searchConfig.headerSelector);
    }
  }, [storeConfig]);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>(headerSelector);
    setHeaderElement(header);

    let intervalId: NodeJS.Timeout;

    if (header) {
      // Use a timer to keep checking the header, handles React re-renders, content changes, etc.
      intervalId = setInterval(() => {
        setHeaderElement(document.querySelector<HTMLElement>(headerSelector));
      }, 500);
    }

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [headerSelector]);

  // Removed IntersectionObserver effect entirely

  const updateHeaderState = useCallback(() => {
    // Use the stored element directly
    if (!headerElement) {
      setHeaderState((prev) => ({
        ...prev,
        isVisible: false,
      }));
      return;
    }

    const rect = headerElement.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(headerElement);

    // We consider it visible if it has height and its bottom edge is on screen.
    // For a top-sticky header, rect.bottom will always be > 0.
    // For a static header scrolled off screen, rect.bottom will be < 0.
    if (rect.height > 0 && rect.bottom > 0) {
      const newState = {
        bottom: rect.bottom,
        height: rect.height,
        isVisible: true,
        isInViewport: true,
        position: computedStyle.position,
      };

      setHeaderState(newState);
    } else {
      // If header is scrolled off screen or hidden
      setHeaderState((prev) => ({
        ...prev,
        isVisible: false,
      }));
    }
  }, [headerElement]);

  useEffect(() => {
    if (!headerElement) return;

    // Initial check
    updateHeaderState();

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderState();
    });

    const mutationObserver = new MutationObserver(() => {
      updateHeaderState();
    });

    const resizeHandler = () => {
      updateHeaderState();
    };

    resizeObserver.observe(headerElement);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener('resize', resizeHandler, { passive: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', resizeHandler);
    };
  }, [updateHeaderState, headerElement]);

  return {
    topStyle: getTopStyle(),
  };
}
